import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const DetailsWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
`;

export const DetailsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};
  gap: ${({ theme }) => theme.landingSpacing.md};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitle = styled.h1`
  font-weight: 500;
  font-size: 1.75rem;
  line-height: 1.2;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
  letter-spacing: -0.025em;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 1.5rem;
  }
`;

export const SectionDescription = styled.p`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-wrap: wrap;
  margin-top: 2px;
`;

export const MetaItem = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
  }
`;

export const DashboardButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    border-color: ${({ theme }) => theme.landing.text};
  }
  &:active {
    transform: scale(0.98);
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
  }
`;

/* ── Section wrapper reused for the document list ─────────────────── */

export const Section = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  padding: ${({ theme }) => theme.landingSpacing.lg};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const SectionLabel = styled.h2`
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  margin: 0 0 ${({ theme }) => theme.landingSpacing.md} 0;
`;

/* ── Document cards grid — same auto-fill card pattern as
     AnalysisItemGrid / ExtractionFieldGrid, adapted for cards ── */

export const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.landingSpacing.md};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const DocumentCard = styled.div`
  background: ${({ theme }) => theme.landing.background};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.landing.buttonBg};
  }
`;

export const DocumentCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

export const DocumentIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.landing.iconBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  color: ${({ theme }) => theme.landing.buttonBg};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const DocumentTitleGroup = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const DocumentName = styled.span`
  font-weight: 500;
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StatusBadge = styled.span`
  align-self: flex-start;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 99px;
  background: rgba(99, 153, 34, 0.12);
  color: #639922;

  ${({ $status }) =>
    $status !== 'COMPLETE' &&
    `
      background: rgba(239, 159, 39, 0.12);
      color: #854F0B;
    `}
`;

export const ConfidenceBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  background: ${({ $score }) =>
    $score >= 80
      ? 'rgba(99,153,34,0.12)'
      : $score >= 50
        ? 'rgba(239,159,39,0.12)'
        : 'rgba(226,75,74,0.12)'};
  color: ${({ $score }) => ($score >= 80 ? '#639922' : $score >= 50 ? '#854F0B' : '#A32D2D')};
`;

export const DocumentReason = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0;
`;

export const DocumentCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: auto;
`;

export const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: ${({ theme }) => theme.landing.buttonBg};
  border: 1px solid ${({ theme }) => theme.landing.buttonBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.buttonBg};
    color: #fff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:active {
    transform: scale(0.98);
  }

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem;
  font-size: 14px;
  color: ${({ theme }) => theme.landing.textSecondary};

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem 2rem;
  text-align: center;
  color: ${({ theme }) => theme.landing.textSecondary};
  font-size: 14px;
`;

export const GlobalError = styled.div`
  background: var(--color-background-danger);
  border: 0.5px solid var(--color-border-danger);
  border-radius: var(--border-radius-md);
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-danger);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};
`;
